import { useCallback, useMemo } from 'react'
import qs from 'qs'
import useQueryParams from 'hooks/useQueryParams'
import { pushWithRegion } from 'actions/NavigationActions'
import { SEARCH_VERTICALS } from 'constants/search'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useCruiseSearchEnabled, useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import CruisesTabContent from '../Components/Mobile/TabContents/CruisesTabContent'
import LineShipIcon from 'components/Luxkit/Icons/line/LineShipIcon'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import * as Analytics from 'analytics/analytics'
import { searchFromHomepageEvent } from 'analytics/optimizely/events'
import { getCruiseSearchStateFromParams } from 'lib/cruises/cruiseUtils'

function useGlobalSearchVerticalTabCruises(searchVerticalItem?: App.SearchVerticalItem): App.GlobalSearchVerticalTabV2 {
  const dispatch = useAppDispatch()

  const isEnabled = useCruiseSearchEnabled()
  const pathname = useAppSelector(state => state.router.location.pathname)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  const queryParams = useQueryParams()
  const initialState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [queryParams])
  const {
    globalCruisesSearchDispatch: searchDispatch,
    globalCruisesSearchState: state,
  } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialState,
  })

  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    cruiseLines,
    flexibleMonths,
    durationMax,
    durationMin,
  } = state

  const onSearch = useCallback(() => {
    const query = qs.stringify({
      destinationId: searchItem?.value,
      destinationIds: searchItems?.map(item => item.value),
      destinationNames: searchItems?.map(item => item.format.mainText),
      destinationName: searchItem?.format.mainText,
      portId: secondarySearchItem?.value,
      portName: secondarySearchItem?.format.mainText,
      cruiseLines,
      departureMonths: flexibleMonths || undefined,
      durationMax,
      durationMin,
    }, { arrayFormat: 'repeat' })

    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }
    dispatch(pushWithRegion('/search/cruises', query))
  }, [searchItem, searchItems, secondarySearchItem, cruiseLines, flexibleMonths, durationMax, durationMin, pathname, regionCode, dispatch])

  return useMemo(() => ({
    isEnabled: isEnabled && !!searchVerticalItem,
    label: searchVerticalItem?.label ?? 'cruises',
    key: SEARCH_VERTICALS.CRUISES,
    context: { dispatch: searchDispatch, state },
    onSearch,
    ContentComponent: CruisesTabContent,
    Icon: LineShipIcon,
  }), [isEnabled, searchVerticalItem, searchDispatch, state, onSearch])
}

export default useGlobalSearchVerticalTabCruises
